import { type FC } from 'react'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'
import debounce from 'lodash/debounce'

import { observer } from 'mobx-react'

import SortableBoxList from 'modules/core/components/SortableBoxList'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import useBulkUpdateLevels from 'modules/job_catalog/hooks/useBulkUpdateLevels'
import { Level } from 'modules/job_catalog/hooks/useRoleLevels'

import Item from './Item'

interface Props {
  levels?: Level[]
  isFetching: boolean
  handleEdit: (level: Level) => void
  handleDelete: (level: Level) => void
  onUpdated: () => void
}

const RoleLevelsSortableList: FC<Props> = ({
  levels,
  isFetching,
  handleEdit,
  handleDelete,
  onUpdated,
}) => {
  const { bulkUpdateLevels, isMutating } = useBulkUpdateLevels()
  const { editJobRoleLevelNameStart, archiveJobRoleLevelStart } = trackingEvents.jobCatalog

  const handleSortEnd = async (sortedLevels: Partial<Level>[]) => {
    const nextLevels = sortedLevels.map((x, i) => ({ id: x.id as string, name: x.name, order: i }))
    await bulkUpdateLevels(nextLevels)
    onUpdated()
  }

  const debouncedHandleSortEnd = debounce(handleSortEnd, 1500)

  const handleRenderItem = (level: Level) => (
    <Item
      label={level.name}
      isArchived={level.archived}
      onEdit={() => {
        handleEdit(level)
        track(editJobRoleLevelNameStart)
      }}
      onDelete={() => {
        handleDelete(level)
        track(archiveJobRoleLevelStart)
      }}
    />
  )

  if (isMutating || isFetching) {
    return (
      <Box alignItems="center" paddingY="s24">
        <Spinner />
      </Box>
    )
  }

  if (!isMutating && !isFetching && !!levels && !levels.length) {
    return (
      <Box justifyContent="center" alignItems="center" padding="s24" flexGrow>
        <Text.Normal color="grey600" weight="medium" size="200">
          {i18n.t('job_catalog.no_supervisors_assigned')}
        </Text.Normal>
      </Box>
    )
  }

  return (
    <SortableBoxList
      items={levels?.filter((level) => !level.isDefault).sort((a, b) => a.order - b.order) || []}
      itemAsKey={(x) => x.id.toString()}
      renderItem={handleRenderItem}
      isDisabled={(x) => x.archived}
      onSortEnd={debouncedHandleSortEnd}
      emptyMessage={i18n.t('job_catalog.job_levels_empty_message')}
    />
  )
}

export default observer(RoleLevelsSortableList)
